import { inject, Injectable } from '@angular/core';
import { FormControl, FormControlStatus, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  startWith,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';

import {
  AttachmentParentType,
  HeadlineLanguage,
  LinkedItem,
  UserListModel,
  UserViewModel,
} from '@ninety/mobile/platform/api';
import { AttachmentsComponent, LinkedItemActions } from '@ninety/mobile/platform/components';
import {
  applyToolStateToForm,
  CurrentUserSelectors,
  CurrentUserTeamSelectors,
  extractValueFromStore,
  LanguageSelectors,
  selectCreateButtonText,
  selectHeadlineToolState,
  selectLinkedItem,
  UniversalCreateActions,
  UserListSelectors,
} from '@ninety/mobile/platform/state';

import { HeadlinesActions, HeadlinesSelectors, HeadlinesSharedApiService } from '../_state';
import { HeadlineModel } from '../_state/models';

import { InitialFormValues } from './create-headline.model';

@Injectable()
export class CreateHeadlineStore {
  private readonly store = inject(Store);
  private readonly headlinesAPIService = inject(HeadlinesSharedApiService);

  readonly form = new FormGroup({
    title: new FormControl<string>(InitialFormValues.title, Validators.required),
    description: new FormControl<string>(InitialFormValues.description),
    teamId: new FormControl<string>(InitialFormValues.teamId),
    userId: new FormControl<string>(InitialFormValues.userId, Validators.required),
    teams: new FormControl<string[]>(InitialFormValues.teams, Validators.required),
    createLinkedItem: new FormControl<boolean>(InitialFormValues.createLinkedItem),
  });

  private readonly createButtonText$ = this.store.select(selectCreateButtonText);
  private readonly currentUser$ = this.store.select(CurrentUserSelectors.selectUser);
  private readonly language$ = this.store.select(LanguageSelectors.headlineLanguage);
  private readonly linkedItem$ = this.store.select(selectLinkedItem);
  private readonly teams$ = this.store.select(CurrentUserTeamSelectors.selectSortedVisibleTeams);
  private readonly users$ = this.store.select(UserListSelectors.selectAll);
  private readonly headlineSelectedTeam$ = this.store.select(HeadlinesSelectors.selectSelectedTeam);
  private readonly isSubmitted$ = new BehaviorSubject<boolean>(false);

  readonly vm$ = combineLatest([
    this.language$,
    this.linkedItem$,
    this.teams$,
    this.currentUser$,
    this.createButtonText$,
    this.users$,
    this.isSubmitted$,
  ]).pipe(
    distinctUntilChanged(),
    map(([language, linkedItem, teams, currentUser, createButtonText, users, submitted]) => ({
      language,
      languageCapitalized: this.capitalizeLanguage(language),
      linkedItem,
      teams,
      currentUser,
      createButtonText,
      users,
      usersOnTeam: this.setUsersOnTeam(users),
      hasLinkedItem: !!linkedItem,
      isSubmitted: submitted,
    }))
  );

  private subscriptions?: Subscription;

  public init(): void {
    this.resetForm();
    this.setToolState();
    this.subscribe();
  }

  public destroy(): void {
    this.unsubscribe();
  }

  public createHeadline(linkedItem?: LinkedItem, attachments?: AttachmentsComponent): void {
    if (this.form.invalid) {
      this.isSubmitted$.next(true);
      this.store.dispatch(UniversalCreateActions.submitted());
      return;
    }

    this.store.dispatch(UniversalCreateActions.isCreating({ creating: true }));

    const [dispatchSource, toastMessage] = ['CreateHeadlineStore', `${this.getLanguage()?.item} created!`];

    const subscription = combineLatest([this.currentUser$, this.vm$])
      .pipe(
        filter(([user]) => !!user?.company?._id),
        map(([user, { languageCapitalized }]) => {
          const { item } = languageCapitalized;
          const creationMessage = `Creating ${item}...`;
          this.store.dispatch(UniversalCreateActions.creationMessage({ creationMessage }));
          return this.getFormAsRequest(user as UserViewModel, linkedItem);
        }),
        switchMap(request => {
          return this.headlinesAPIService.createHeadline(request).pipe(
            catchError(err => {
              this.store.dispatch(UniversalCreateActions.error({ error: err }));
              return EMPTY;
            })
          );
        }),
        tap(response => {
          const headline = response[0]; // API returns an array containing just the headline.
          this.store.dispatch(HeadlinesActions.universalCreateSuccess({ headline, dispatchSource, toastMessage }));

          // If this was created from a linked item, track the event
          if (linkedItem && this.form.controls.createLinkedItem.value) {
            this.store.dispatch(
              LinkedItemActions.linkedItemCreated({
                entityId: headline._id as string,
                entityType: 'headline',
                parentEntity: linkedItem.type,
                parentEntityId: linkedItem.id,
              })
            );
          }
        })
      )
      .subscribe(response => {
        const createdHeadline = response?.[0];

        if (!createdHeadline?._id || !attachments?.hasDeferredAttachments()) {
          this.store.dispatch(UniversalCreateActions.close({ dispatchSource, toastMessage }));
          return;
        }

        this.store.dispatch(UniversalCreateActions.creationMessage({ creationMessage: 'Attaching Files...' }));

        attachments.attachDeferredFiles({
          parentId: createdHeadline._id,
          parentType: AttachmentParentType.Headline,
          closeUniversalCreate: true,
          completionToastMessage: toastMessage,
          dispatchSource,
        });
      });

    this.add(subscription);
  }

  private getFormAsRequest(user: UserViewModel, linkedItem?: LinkedItem): HeadlineModel {
    return {
      title: this.form.controls.title.value,
      description: this.form.controls.description.value,
      isCascadedMessage: false,
      teamIds: this.form.controls.teams.value,
      ownedByUserId: this.form.controls.userId.value,
      from: linkedItem && this.form.controls.createLinkedItem.value ? linkedItem : undefined,
    } as HeadlineModel;
  }

  private getLanguage(): HeadlineLanguage | undefined {
    let language: HeadlineLanguage | undefined;
    this.vm$.pipe(take(1)).subscribe(vm => (language = vm.languageCapitalized));
    return language;
  }

  private resetForm(): void {
    this.form.patchValue({ ...InitialFormValues });
    this.isSubmitted$.next(false);
  }

  private subscribe(): void {
    this.unsubscribe();

    this.add(
      this.form.statusChanges
        .pipe(
          startWith('INVALID' as FormControlStatus),
          distinctUntilChanged(),
          map(status => status === 'VALID')
        )
        .subscribe(enable => this.store.dispatch(UniversalCreateActions.enable({ enable })))
    );

    this.add(
      combineLatest([
        this.form.controls.description.valueChanges.pipe(startWith(this.form.controls.description.value)),
        this.form.controls.userId.valueChanges.pipe(startWith(this.form.controls.userId.value)),
        this.form.controls.title.valueChanges.pipe(startWith(this.form.controls.title.value)),
        this.form.controls.teams.valueChanges.pipe(startWith(this.form.controls.teams.value)),
      ])
        .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
        .subscribe(([description, userId, title, teams]) => {
          this.store.dispatch(
            UniversalCreateActions.updateToolState({
              feature: 'headline',
              state: { description: description ?? '', teams: teams ?? [], userId: userId ?? '', title: title ?? '' },
            })
          );
          this.form.controls.teams.setValue(teams ?? [], { emitEvent: false });
        })
    );

    this.add(
      combineLatest([this.vm$, this.linkedItem$, this.createButtonText$, this.form.valueChanges])
        .pipe(distinctUntilChanged())
        .subscribe(([{ languageCapitalized }, linkedItem, currentText]) => {
          const creatingLinkedItem = !!linkedItem && !!this.form.controls.createLinkedItem.value;
          const createButtonText = !creatingLinkedItem
            ? `Create ${languageCapitalized.item}`
            : `Create Linked ${languageCapitalized.item}`;
          if (currentText !== createButtonText)
            this.store.dispatch(UniversalCreateActions.createButtonText({ createButtonText }));
        })
    );

    this.add(
      this.linkedItem$.pipe(distinctUntilChanged()).subscribe(linkedItem => {
        this.form.controls.createLinkedItem.setValue(!!linkedItem);
        if (linkedItem) this.form.controls.createLinkedItem.enable();
        else this.form.controls.createLinkedItem.disable();
      })
    );
  }

  private add(subscription: Subscription): void {
    this.subscriptions ??= new Subscription();

    this.subscriptions.add(subscription);
  }

  private capitalize(words?: string): string {
    if (!words?.trim()) return '';
    return words
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private capitalizeLanguage(language: HeadlineLanguage): HeadlineLanguage {
    return {
      item: this.capitalize(language.item),
      items: this.capitalize(language.items),
    };
  }

  private setUsersOnTeam(users: UserListModel[]): UserListModel[] {
    const teamId = this.form.controls.teamId.value;

    if (!teamId || !users.length) {
      return users;
    }

    const usersOnTeam = users.filter(user => user.teamIds.some(id => id === teamId));
    const userId = this.form.controls.userId.value;

    if (userId) {
      const userFoundOnTeam = usersOnTeam.find(user => user._id === userId);
      if (!userFoundOnTeam) {
        this.form.controls.userId.setValue('');
      }
    }

    return usersOnTeam;
  }

  private unsubscribe(): void {
    this.subscriptions?.unsubscribe();
    this.subscriptions = undefined;
  }

  private setToolState(): void {
    const toolState = extractValueFromStore(this.store, selectHeadlineToolState);
    const currentUserId = extractValueFromStore(this.store, CurrentUserSelectors.selectId);

    applyToolStateToForm(this.form, toolState, {
      userId: currentUserId,
    });
  }
}
